import { Mobile } from './mobile';
import type { EntityComponents, RenderableComponent } from './entity';
import type { ArpgItem } from './arpgItem';
// Type-only import (erased at runtime), so persistence.ts importing this module's
// values back creates no runtime cycle.
import type { EntitySnapshot } from './persistence';

/**
 * The **component descriptor table** (chassis Slice A, PRD #534 D4, ADR-0260):
 * every {@link Entity} component declared **once** - its key, its
 * serialize/rebuild shape for persistence, and (via
 * {@link CODEC_KNOWN_COMPONENTS}) its binary-codec bit - from which
 * `serializeEntity`/`rebuildEntity` (`persistence.ts`) and the codec's known
 * set (`binaryCodec.ts`) are derived.
 *
 * This mechanizes the convention that kept failing manually (the
 * persistence-serializer gotcha): a component added to `entity.ts` but
 * forgotten in one of the hand-maintained mirror lists silently vanished from
 * saves or shipped only via the wire codec's `extras`. Now:
 *
 * - **Adding a component to `entity.ts` without a descriptor is a compile
 *   error.** {@link COMPONENT_DESCRIPTORS} is a mapped record over every
 *   component key of both {@link EntityComponents} and {@link EntitySnapshot}
 *   (the {@link ComponentKey} equality assert below), so `tsc --noEmit` - a CI
 *   step - fails naming the missing key.
 * - **Runtime tables are cross-checked loudly.** {@link descriptorConsistencyErrors}
 *   runs at module load (and in `componentDescriptors.test.ts` with mismatched
 *   fixtures), so a codec bit naming an undeclared component throws on first
 *   import rather than silently corrupting frames.
 *
 * Two orders are load-bearing and **append-only**:
 *
 * - The record's **declaration order** is the serialized JSON key order
 *   (persistence output must stay byte-identical for existing saves) - append
 *   new components at the end, never reorder.
 * - {@link CODEC_KNOWN_COMPONENTS} array index is the wire bitmask **bit index**
 *   (ADR-0190) - append, never reorder. A component absent from it still
 *   round-trips the wire via the codec's `extras` escape hatch (no drop), so
 *   "declare the descriptor now, claim a codec bit later" is safe.
 */

/** Every component key an entity/snapshot can carry (everything except `id`). */
export type ComponentKey = keyof EntityComponents;

/**
 * Compile-time gate: the {@link EntitySnapshot} component keys must be exactly
 * the {@link Entity} component keys. If `entity.ts` gains a component that
 * `persistence.ts`'s snapshot shape lacks (or vice versa), this line fails
 * `tsc --noEmit` naming the stray key.
 */
type SnapshotComponentKey = Exclude<keyof EntitySnapshot, 'id'>;
type MutuallyAssignable<A, B> = [A] extends [B] ? ([B] extends [A] ? true : never) : never;
const _entityAndSnapshotKeysAgree: MutuallyAssignable<ComponentKey, SnapshotComponentKey> = true;
void _entityAndSnapshotKeysAgree;

/**
 * How one component crosses the persistence boundary: `serialize` turns the
 * live component into its plain snapshot value (a deep-enough copy that the
 * snapshot is independent of later world mutation), `rebuild` turns the plain
 * value back into the live one. For every plain-data component the two are the
 * same clone; the one live-class component ({@link Mobile}) maps through
 * `toState`/`fromState`.
 */
export interface ComponentDescriptor<K extends ComponentKey = ComponentKey> {
  serialize: (live: NonNullable<EntityComponents[K]>) => NonNullable<EntitySnapshot[K]>;
  /**
   * Optional **wire-only** serializer (PRD #825), used by `serialize(world, {
   * wire: true })` - the broadcast path - in place of {@link serialize}. It
   * exists for fields that are authoritative server-side steering the client
   * never reads but which dominate delta bytes; today only `mobile` declares
   * one, to leave its remaining walk route at home. A wire serializer must
   * produce a value {@link rebuild} still accepts, so the read-model rebuilds
   * from it exactly as it does from a saved snapshot.
   */
  wire?: (live: NonNullable<EntityComponents[K]>) => NonNullable<EntitySnapshot[K]>;
  rebuild: (snap: NonNullable<EntitySnapshot[K]>) => NonNullable<EntityComponents[K]>;
}

function cloneRenderable(r: RenderableComponent): RenderableComponent {
  return {
    ...r,
    ...(r.equipment ? { equipment: r.equipment.map((item) => ({ ...item })) } : {}),
  };
}

/**
 * The table itself: one entry per component, keyed exhaustively (a missing or
 * stray key is a compile error - see the module doc).
 *
 * **Declaration order is the persisted JSON key order** - the order
 * `serializeEntity` has always written - so existing saves stay byte-identical.
 * Append new components at the end.
 */
/** A detached copy of one rolled item, affix list included. */
function cloneArpgItem(item: ArpgItem): ArpgItem {
  return { ...item, affixes: (item.affixes ?? []).map((affix) => ({ ...affix })) };
}

export const COMPONENT_DESCRIPTORS: { readonly [K in ComponentKey]: ComponentDescriptor<K> } = {
  position: { serialize: (p) => ({ ...p }), rebuild: (p) => ({ ...p }) },
  renderable: { serialize: cloneRenderable, rebuild: cloneRenderable },
  mobile: {
    serialize: (m) => m.toState(),
    wire: (m) => m.toState({ path: false }),
    rebuild: (s) => Mobile.fromState(s),
  },
  door: { serialize: (d) => ({ ...d }), rebuild: (d) => ({ ...d }) },
  dice: { serialize: (d) => ({ ...d }), rebuild: (d) => ({ ...d }) },
    teleporter: { serialize: (t) => ({ to: { ...t.to } }), rebuild: (t) => ({ to: { ...t.to } }) },
    moongate: {
      serialize: (m) =>
        m.to ? { to: { ...m.to } } : m.purpose ? { purpose: m.purpose } : {},
      rebuild: (m) =>
        m.to ? { to: { ...m.to } } : m.purpose ? { purpose: m.purpose } : {},
  },
  chest: { serialize: (c) => ({ ...c }), rebuild: (c) => ({ ...c }) },
  banish: { serialize: (b) => ({ ...b }), rebuild: (b) => ({ ...b }) },
  rat: { serialize: () => true, rebuild: () => true },
  health: { serialize: (h) => ({ ...h }), rebuild: (h) => ({ ...h }) },
  vortex: { serialize: () => true, rebuild: () => true },
  horde: { serialize: () => true, rebuild: () => true },
  death: { serialize: (d) => ({ ...d }), rebuild: (d) => ({ ...d }) },
  justice: { serialize: (j) => j, rebuild: (j) => j },
  thiefCorpse: { serialize: () => true, rebuild: () => true },
  escort: { serialize: (e) => ({ ...e }), rebuild: (e) => ({ ...e }) },
  arenaMaster: { serialize: () => true, rebuild: () => true },
  projectile: { serialize: (p) => ({ ...p }), rebuild: (p) => ({ ...p }) },
  xpGem: { serialize: (g) => ({ ...g }), rebuild: (g) => ({ ...g }) },
  pickup: { serialize: (p) => ({ ...p }), rebuild: (p) => ({ ...p }) },
  interaction: { serialize: (i) => ({ ...i }), rebuild: (i) => ({ ...i }) },
  dungeonLoot: {
    // The rolled affix list is copied rather than shared: a snapshot that
    // handed out a pointer into a live item would let one consumer's edit
    // reach every other holder of that drop.
    serialize: (loot) => ({ ...loot, item: cloneArpgItem(loot.item) }),
    rebuild: (loot) => ({ ...loot, item: cloneArpgItem(loot.item) }),
  },
  dungeonEnemy: { serialize: () => true, rebuild: () => true },
  dungeonGlobe: { serialize: (g) => ({ ...g }), rebuild: (g) => ({ ...g }) },
  dungeonWaypoint: { serialize: (w) => ({ ...w }), rebuild: (w) => ({ ...w }) },
  trainingLoop: { serialize: (t) => ({ ...t }), rebuild: (t) => ({ ...t }) },
  soulstoneKeeper: { serialize: () => true, rebuild: () => true },
};

/**
 * The component keys in declaration (= persisted) order. `Object.keys`
 * preserves string-key insertion order, so this is exactly the table's order.
 */
export const COMPONENT_KEYS = Object.keys(COMPONENT_DESCRIPTORS) as readonly ComponentKey[];

/**
 * The components the binary wire codec (`src/net/binaryCodec.ts`, ADR-0190)
 * encodes **positionally**: the array index is the wire bitmask bit index, so
 * this list is **append-only** - never reorder, never remove (the shipped
 * 17-bit prefix is pinned by test). A descriptor absent from this list still
 * round-trips the wire losslessly via the codec's `extras` section (`justice`,
 * `thiefCorpse`, `escort`, `arenaMaster` ride there today); claiming a bit
 * here is a wire-size optimization, not a correctness requirement.
 *
 * The `satisfies` clause keeps every element a real {@link ComponentKey}
 * (compile error otherwise) while preserving the literal tuple type the
 * codec's per-component switch is exhaustively checked against.
 */
export const CODEC_KNOWN_COMPONENTS = [
  'position',
  'renderable',
  'mobile',
  'door',
  'dice',
  'teleporter',
  'rat',
  'health',
  'vortex',
  'horde',
  'death',
  'moongate',
  'chest',
  'banish',
  'projectile',
  'xpGem',
  'pickup',
  // Appended past the pinned 17-bit ADR-0190 prefix (PRD #534 D5, ADR-0266).
  'interaction',
] as const satisfies readonly ComponentKey[];

/** A component the codec encodes positionally (its bit is its array index). */
export type CodecKnownComponent = (typeof CODEC_KNOWN_COMPONENTS)[number];

/**
 * The pure consistency check behind the CI gate: given the descriptor keys and
 * the codec's known-component order, return every disagreement as a message
 * that **names the component and the site to fix**. Empty array = consistent.
 * Exposed as a pure function (rather than only a module-load assert) so the
 * test suite can feed it deliberately-mismatched fixtures.
 */
export function descriptorConsistencyErrors(
  descriptorKeys: readonly string[],
  codecOrder: readonly string[],
): string[] {
  const errors: string[] = [];
  const seenDescriptors = new Set<string>();
  for (const key of descriptorKeys) {
    if (seenDescriptors.has(key)) {
      errors.push(
        `component '${key}' has a duplicate descriptor - declare it exactly once in ` +
          `COMPONENT_DESCRIPTORS (src/sim/componentDescriptors.ts)`,
      );
    }
    seenDescriptors.add(key);
  }
  const seenBits = new Set<string>();
  for (let bit = 0; bit < codecOrder.length; bit++) {
    const key = codecOrder[bit]!;
    if (seenBits.has(key)) {
      errors.push(
        `component '${key}' claims a duplicate codec bit (${bit}) - each component appears ` +
          `at most once in CODEC_KNOWN_COMPONENTS (src/sim/componentDescriptors.ts)`,
      );
    }
    seenBits.add(key);
    if (!seenDescriptors.has(key)) {
      errors.push(
        `codec bit ${bit} names '${key}' but no descriptor declares it - add '${key}' to ` +
          `COMPONENT_DESCRIPTORS in src/sim/componentDescriptors.ts`,
      );
    }
  }
  return errors;
}

// Loud module-load gate: an inconsistent table can never run quietly - the first
// import (every test file, the server boot, the client bundle) throws with the
// per-component messages.
{
  const errors = descriptorConsistencyErrors(COMPONENT_KEYS, CODEC_KNOWN_COMPONENTS);
  if (errors.length > 0) {
    throw new Error(`component descriptor table inconsistent:\n${errors.join('\n')}`);
  }
}
